"use client";
import { IconButton } from "@cmp-src/primitives/icon-button/IconButton";
import { TooltipProvider } from "@cmp-src/primitives/tooltip";
import {
  AgendaDay as AgendaDayType,
  IWorkingLocationPresenceType,
  WL_PRESENCE_OFFICE
} from "@gettactic/api";
import { getObjectKeys, parseApiDate } from "@gettactic/helpers";
import { format, isPast, isToday, isWeekend } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import { useDashboardFilters } from "@/features/dashboard/DashboardStateContext";
import { useDashboardLocation } from "@/features/dashboard/hooks/useDashboardLocation";
import { AuthenticatedUserRow } from "@/features/teams/components/TeamSchedule/AuthenticatedUserRow";
import { api } from "@/lib/api/api";
import { dateParseISO } from "@/lib/utils";
import { AgendaDay } from "./AgendaDay";

const weekDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];

export interface AgendaWeekProps {
  locationData: ReturnType<typeof useDashboardLocation>;
  showAsMobileDaily?: boolean;
  selectedDayIndex?: number;
  setSelectedDayIndex?: (index: number) => void;
  daysCount?: number;
  onPrevWeek?: () => void;
  onNextWeek?: () => void;
}

const AgendaWeekContent: React.FC<AgendaWeekProps> = ({
  locationData,
  showAsMobileDaily,
  selectedDayIndex,
  setSelectedDayIndex,
  daysCount,
  onPrevWeek,
  onNextWeek
}) => {
  const {
    scheduleMeData,
    officeAvailabilityData,
    isLoadingOfficeAvailability,
    updateWorkingLocation,
    getLocationForDate,
    isLocationLoading,
    weekDate,
    authenticatedUser,
    dedicatedDesk,
    isDeskReleasedOn,
    areDeskReleasesKnown,
    areDeskReleasesLoading
  } = locationData;

  const days = getObjectKeys(scheduleMeData.data?.days || {});

  const isTodayInWeek = days.some((d) => isToday(parseApiDate(d)));
  const isTodayWeekend = isWeekend(new Date());
  const shouldShowTodayBadge = !showAsMobileDaily && isTodayInWeek;
  const shouldApplyCollapseRules = shouldShowTodayBadge && !isTodayWeekend;

  const { selectedEvent, setSelectedEvent } = useDashboardFilters();
  const mobileDailyRef = React.useRef<HTMLDivElement>(null);

  const hasAnyAgendaLocationAvailable =
    scheduleMeData.data?.agenda &&
    Object.keys(scheduleMeData.data?.agenda).some(
      (date) => scheduleMeData.data?.agenda[date]?.location !== "not_available"
    );

  return (
    <>
      {showAsMobileDaily && (
        <div
          className={
            "w-full max-w-full overflow-auto rounded-lg border-1 border-neutral-200 bg-white shadow-sm"
          }
        >
          <div className="flex items-center justify-between px-2 pt-2">
            <IconButton
              icon={ChevronLeft}
              variant="ghost"
              size="md"
              iconClassName="h-4 w-4"
              onClick={onPrevWeek}
              aria-label="Previous week"
            />
            <h2 className="font-medium text-sm">
              {format(dateParseISO(weekDate), "MMMM yyyy")}
            </h2>
            <IconButton
              icon={ChevronRight}
              variant="ghost"
              size="md"
              iconClassName="h-4 w-4"
              onClick={onNextWeek}
              aria-label="Next week"
            />
          </div>
          <div className={"flex w-full gap-0 px-0"} ref={mobileDailyRef}>
            <TooltipProvider>
              <AuthenticatedUserRow
                authenticatedUser={authenticatedUser}
                headers={[]}
                favsApi={api}
                columnCount={daysCount || 0}
                selectedDayIndex={selectedDayIndex}
                setSelectedDayIndex={setSelectedDayIndex}
                showMobileCells
              />
            </TooltipProvider>
          </div>
        </div>
      )}
      <div className="md:-mt-[7px] flex flex-col gap-6">
        {days?.slice(0, (weekDays || []).length).map((date, index) => {
          if (
            hasAnyAgendaLocationAvailable &&
            scheduleMeData?.data?.agenda?.[date]?.location === "not_available"
          )
            return null;
          if (showAsMobileDaily && index !== selectedDayIndex) return;
          // We have to cast here because there is an agenda for each day
          const todaysAgenda = scheduleMeData.data?.agenda[
            date
          ] as AgendaDayType;
          const todaysOfficeAvailability =
            officeAvailabilityData?.days?.[date]?.[
              todaysAgenda?.office_id ?? ""
            ] ?? null;
          const location = todaysAgenda?.location;
          const officeId = todaysAgenda?.office_id;

          // Get the effective location data (actual or optimistic)
          const effectiveDay = getLocationForDate(
            date,
            scheduleMeData.data?.agenda[date] || null
          );

          const isLoading = isLocationLoading(date);

          const parsedDate = parseApiDate(date);
          const isDayToday = isToday(parsedDate);
          const isDayPast = isPast(parsedDate) && !isDayToday;
          const isCollapsible = shouldApplyCollapseRules && isDayPast;
          const showTodayBadge = shouldShowTodayBadge && isDayToday;

          return (
            <AgendaDay
              key={`dash_schedule_${date}_${location}`}
              office_id={officeId}
              date={date}
              location={
                location !== WL_PRESENCE_OFFICE
                  ? (location as IWorkingLocationPresenceType)
                  : null
              }
              workingLocationId={officeId ?? undefined}
              schedule={scheduleMeData.data?.agenda[date]?.schedules ?? []}
              onSelectItemChange={setSelectedEvent}
              selectedEvent={selectedEvent}
              day={todaysAgenda || null}
              isLoadingOfficeAvailability={isLoadingOfficeAvailability}
              officeAvailability={todaysOfficeAvailability}
              onLocationChange={(newLocation) => {
                updateWorkingLocation(newLocation, date, index, todaysAgenda);
              }}
              isLocationLoading={isLoading}
              optimisticLocation={
                effectiveDay &&
                todaysAgenda &&
                effectiveDay.location !== todaysAgenda.location
                  ? effectiveDay
                  : effectiveDay?.office_id !== todaysAgenda.office_id
                    ? effectiveDay
                    : null
              }
              isMobile={showAsMobileDaily}
              orgSlug={authenticatedUser.slug ?? undefined}
              isCollapsible={isCollapsible}
              showTodayBadge={showTodayBadge}
              dedicatedDesk={dedicatedDesk}
              isDeskReleased={isDeskReleasedOn(date)}
              areDeskReleasesKnown={areDeskReleasesKnown}
              areDeskReleasesLoading={areDeskReleasesLoading}
            />
          );
        })}
      </div>
    </>
  );
};

export const AgendaWeek: React.FC<AgendaWeekProps> = (props) => {
  return <AgendaWeekContent {...props} />;
};
